/**
 * CREATIVE DETONATOR - Storyboard UI
 * Main module that coordinates all storyboard sub-modules
 *
 * v4 - Simplified: removed setting/subject/action/mood, copy button inline with save
 * v91 - Fixed: Show editing overlay only on submit, not on modal open
 * v96 - Fixed: Auto-import panels when viewing storyboard (not just SCENES)
 * v97 - REFACTORED: Split into modular sub-files (Phase 2 refactoring)
 * v98 - UI: Panel Library merged into Storyboard tab (no separate PANELS tab)
 */

import { GeneratorState } from '../core/state.js';
import { copyToClipboardWithFeedback } from '../core/utils.js';

// State imports
import {
  getStoryboard,
  setStoryboard,
  getActiveStoryboard,
  setActiveIdeaId,
  getActiveIdeaId,
  updateSceneField,
  updateStoryboardStyle,
  saveToDatabase,
  isDatabaseIdea,
  setPanelEditingStatus
} from './state.js';
import { generateScenes } from './api.js';

// Sub-module imports
import {
  renderVideosTab,
  renderVideosView,
  initVideosView,
  updateVideoTabCount,
  setRefreshSceneCardCallback as setVideosRefreshCallback
} from './ui/videos.js';

import {
  renderPanelsView,
  handlePanelsViewClick,
  handlePanelsViewChange,
  showPanelPicker,
  closePanelPicker,
  handleAssignPanel,
  handleImportPanels,
  autoImportPanels,
  assignPanelsToScenes,
  importPanelsToScenes,
  showPanelEditingOverlay,
  hidePanelEditingOverlay,
  isSplittingInProgress,
  setRenderStoryboardCallback as setPanelsRenderCallback
} from './ui/panels.js';

import {
  renderStoryboard,
  renderSkeletonUI,
  renderSceneCard,
  renderFrameSlot,
  formatCameraText,
  formatAudioText,
  selectScene,
  navigateScene,
  copyScenePrompt,
  togglePromptExpansion,
  saveSceneChanges,
  generateFrame,
  handleGenerateSceneVideo,
  handlePreviewSceneVideo,
  handleSceneVideoComplete,
  refreshSceneCard,
  handleDuplicateScene,
  handleDeleteScene,
  handleAddScene,
  handleEditFrame,
  showFrameEditingOverlay,
  hideFrameEditingOverlay,
  initSceneAutoSave,
  setRenderStoryboardCallback as setScenesRenderCallback,
  setUpdateVideoTabCountCallback,
  setRenderVideosTabCallback,
  setAutoImportPanelsCallback
} from './ui/scenes.js';

import {
  handleFrameDragOver,
  handleFrameDragLeave,
  handleFrameDrop,
  handleLocalImageUpload,
  setRenderStoryboardCallback as setDragDropRenderCallback
} from './ui/drag-drop.js';
import {
  openPanelSelector,
  closePanelSelector
} from './ui/panel-selector.js';
import { downloadImage } from '../../../js/core/download.js';
import { openPreview } from '../../../js/shared/image-preview.js';

// ============================================================================
// MODULE INITIALIZATION
// ============================================================================

/**
 * Set up cross-module callbacks
 * This wires up the modules so they can communicate with each other
 */
function setupModuleCallbacks() {
  // Videos module needs to call refreshSceneCard
  setVideosRefreshCallback(refreshSceneCard);

  // Panels module needs to call renderStoryboard
  setPanelsRenderCallback(renderStoryboard);

  // Scenes module needs various callbacks
  setScenesRenderCallback(renderStoryboard);
  setUpdateVideoTabCountCallback(updateVideoTabCount);
  setRenderVideosTabCallback(renderVideosTab);
  setAutoImportPanelsCallback(autoImportPanels);

  // Drag-drop module needs to call renderStoryboard
  setDragDropRenderCallback(renderStoryboard);
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize storyboard UI
 * Attaches event handlers to both canvas and modal containers
 */
export function initStoryboardUI() {
  // Set up cross-module communication
  setupModuleCallbacks();

  // Initialize auto-save for scene fields
  initSceneAutoSave();

  // Find all possible scene containers (canvas and modal)
  const containers = [
    document.getElementById('canvas-video-scenes-container'),  // Canvas
    document.getElementById('video-scenes-container')  // Modal
  ].filter(Boolean);

  if (containers.length === 0) {
    console.warn('[Storyboard UI] No video scenes container found');
    return;
  }

  containers.forEach(container => {
    // Skip if already initialized (check for data attribute)
    if (container.dataset.storyboardInitialized) return;
    container.dataset.storyboardInitialized = 'true';

    // Event delegation for all storyboard actions
    container.addEventListener('click', handleStoryboardClick);
    container.addEventListener('input', handleStoryboardInput);
    container.addEventListener('change', handleStoryboardChange);

    // Drag & drop for frame slots
    container.addEventListener('dragover', handleFrameDragOver);
    container.addEventListener('dragleave', handleFrameDragLeave);
    container.addEventListener('drop', handleFrameDrop);
  });

  // Panels view event delegation (PANELS is now a top-level tab)
  initPanelsView();

  // Listen for frame updates from image generation/editing (once only)
  if (!window._storyboardEventsInitialized) {
    window._storyboardEventsInitialized = true;

    window.addEventListener('storyboard:frameUpdated', handleFrameUpdated);
    window.addEventListener('storyboard:panelUpdated', handlePanelUpdated);
    window.addEventListener('storyboard:editSubmitted', handleEditSubmitted);
    window.addEventListener('storyboard:editCanceled', handleEditCanceled);
    document.addEventListener('storyboard:sceneVideoComplete', handleSceneVideoComplete);
  }

  // Initialize videos view event handlers
  initVideosView();

  console.log('[Storyboard UI] Initialized');
}

// ============================================================================
// EVENT HANDLERS
// ============================================================================

/**
 * Handle frame updated event from image generation/editing
 */
function handleFrameUpdated(event) {
  const { ideaId, sceneSeq, frameType, imageUrl } = event.detail;
  console.log('[Storyboard] Frame updated:', sceneSeq, frameType);

  // Hide editing overlay if present
  hideFrameEditingOverlay(sceneSeq, frameType);

  // Re-render the storyboard to show the new image
  renderStoryboard(ideaId);
}

/**
 * Handle panel updated event from contextual editing
 */
function handlePanelUpdated(event) {
  const { ideaId, panelIndex, imageUrl } = event.detail;
  console.log('[Storyboard] Panel updated:', panelIndex + 1);

  // Hide loading overlay
  hidePanelEditingOverlay(panelIndex);

  // Re-render the panels view to show the new image
  renderPanelsView();
}

/**
 * Handle edit submitted event - show loading overlays when user clicks APPLY
 */
function handleEditSubmitted(event) {
  const { ideaId, context } = event.detail;
  console.log('[Storyboard] Edit submitted:', context);

  if (context?.type === 'scene') {
    // Show frame editing overlay
    showFrameEditingOverlay(context.sceneSeq, context.frame);
  } else if (context?.type === 'panel') {
    // Show panel editing overlay
    setPanelEditingStatus(ideaId, context.panelIndex, true);
    showPanelEditingOverlay(context.panelIndex);
  }
}

/**
 * Handle edit canceled event - hide any loading overlays
 */
function handleEditCanceled(event) {
  const { ideaId, context } = event.detail;
  console.log('[Storyboard] Edit canceled:', context);

  if (context?.type === 'scene') {
    // Hide frame editing overlay
    hideFrameEditingOverlay(context.sceneSeq, context.frame);
  } else if (context?.type === 'panel') {
    // Hide panel editing overlay and clear status
    hidePanelEditingOverlay(context.panelIndex);
    setPanelEditingStatus(ideaId, context.panelIndex, false);
  }
}

/**
 * Initialize panels view event handlers
 * Attaches to both modal (#tab-panels) and canvas (data-tab="storyboard" which now includes panels)
 */
function initPanelsView() {
  // Find all possible panels containers (modal and canvas storyboard tab)
  const containers = [
    document.getElementById('tab-panels'),  // Modal
    document.querySelector('.canvas-tab-content[data-tab="storyboard"]')  // Canvas (panels merged into storyboard)
  ].filter(Boolean);

  if (containers.length === 0) {
    console.warn('[Storyboard] No panels container found, panels view handlers not attached');
    return;
  }

  containers.forEach(panelsView => {
    // Skip if already initialized (check for data attribute)
    if (panelsView.dataset.panelsInitialized) return;
    panelsView.dataset.panelsInitialized = 'true';

    // Event delegation for panels view
    panelsView.addEventListener('click', handlePanelsViewClick);
    panelsView.addEventListener('change', handlePanelsViewChange);

    // Drag & drop for adding panels - works on entire library grid area
    panelsView.addEventListener('dragover', (e) => {
      const libraryGrid = e.target.closest('[id$="panel-library-grid"]');
      if (libraryGrid) {
        e.preventDefault();
        libraryGrid.classList.add('drag-over');
      }
    });

    panelsView.addEventListener('dragleave', (e) => {
      const libraryGrid = e.target.closest('[id$="panel-library-grid"]');
      // Only remove if we're actually leaving the grid (not entering a child)
      if (libraryGrid && !libraryGrid.contains(e.relatedTarget)) {
        libraryGrid.classList.remove('drag-over');
      }
    });

    panelsView.addEventListener('drop', (e) => {
      const libraryGrid = e.target.closest('[id$="panel-library-grid"]');
      if (libraryGrid) {
        e.preventDefault();
        libraryGrid.classList.remove('drag-over');
        const files = e.dataTransfer?.files;
        if (files?.length > 0 && files[0].type.startsWith('image/')) {
          // Panel upload is handled in panels module
          const uploadInput = libraryGrid.querySelector('[data-action="upload-panel"]');
          if (uploadInput) {
            // Create a DataTransfer to set files
            const dt = new DataTransfer();
            dt.items.add(files[0]);
            uploadInput.files = dt.files;
            uploadInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    });
  });

  console.log('[Storyboard] Panels view handlers initialized');
}

/**
 * Handle click events on storyboard container
 */
export function handleStoryboardClick(e) {
  const target = e.target.closest('[data-action]');
  if (!target) return;

  e.preventDefault();
  e.stopPropagation();

  const action = target.dataset.action;
  const sceneSeq = parseInt(target.dataset.scene) || 1;

  console.log('[Storyboard] Action:', action, 'Scene:', sceneSeq);

  switch (action) {
    case 'select-scene':
      selectScene(sceneSeq - 1);
      break;
    case 'prev-scene':
      navigateScene(-1);
      break;
    case 'next-scene':
      navigateScene(1);
      break;
    case 'copy-prompt':
      copyScenePrompt(sceneSeq, target);
      break;
    case 'expand-prompt':
      togglePromptExpansion(target);
      break;
    case 'save-scene':
      saveSceneChanges(sceneSeq, target);
      break;
    case 'gen-start-frame':
      generateFrame(sceneSeq, 'start', target);
      break;
    case 'gen-end-frame':
      generateFrame(sceneSeq, 'end', target);
      break;
    case 'gen-video':
      handleGenerateSceneVideo(sceneSeq);
      break;
    case 'preview-scene-video':
      handlePreviewSceneVideo(sceneSeq);
      break;
    case 'regen-scene-video':
      handleGenerateSceneVideo(sceneSeq);
      break;
    case 'show-video-history':
      // Task 113 Issue #6: Switch to VIDEOS tab to show all video versions
      switchToVideosTab();
      break;
    // Panel Manager actions
    case 'import-panels':
      handleImportPanels(target);
      break;
    case 'swap-panel':
      showPanelPicker(sceneSeq, target.dataset.type);
      break;
    case 'assign-panel':
      handleAssignPanel(target);
      break;
    case 'close-picker':
      closePanelPicker();
      break;
    // Scene CRUD actions
    case 'add-scene':
      handleAddScene();
      break;
    case 'duplicate-scene':
      handleDuplicateScene(sceneSeq);
      break;
    case 'delete-scene':
      handleDeleteScene(sceneSeq);
      break;
    // Frame editing action
    case 'edit-frame':
      handleEditFrame(target);
      break;
    // Frame download action
    case 'download-frame':
      handleDownloadFrame(target);
      break;
    // Frame preview action
    case 'preview-frame':
      handlePreviewFrame(target);
      break;
  }
}

/**
 * Handle frame download action
 * Downloads the start or end frame image
 */
async function handleDownloadFrame(target) {
  const sceneSeq = parseInt(target.dataset.scene);
  const frameType = target.dataset.type;

  const storyboard = getActiveStoryboard();
  if (!storyboard) {
    console.warn('[Storyboard] Cannot download frame: no active storyboard');
    return;
  }

  const scene = storyboard.scenes?.find(s => s.seq === sceneSeq);
  const frameUrl = scene?.frames?.[frameType]?.url;

  if (!frameUrl) {
    console.warn('[Storyboard] No frame image to download');
    return;
  }

  // Generate descriptive filename
  const filename = `scene-${sceneSeq}-${frameType}-${Date.now()}`;
  await downloadImage(frameUrl, filename);
  console.log(`[Storyboard] Downloaded ${frameType} frame for scene ${sceneSeq}`);
}

/**
 * Handle frame preview action
 * Opens the image preview modal for a frame
 */
function handlePreviewFrame(target) {
  const sceneSeq = parseInt(target.dataset.scene);
  const frameType = target.dataset.type;

  const storyboard = getActiveStoryboard();
  if (!storyboard) {
    console.warn('[Storyboard] Cannot preview frame: no active storyboard');
    return;
  }

  const scene = storyboard.scenes?.find(s => s.seq === sceneSeq);
  const frameUrl = scene?.frames?.[frameType]?.url;

  if (!frameUrl) {
    console.warn('[Storyboard] No frame image to preview');
    return;
  }

  // Open the image preview modal
  openPreview({
    url: frameUrl,
    caption: `Scene ${sceneSeq} - ${frameType.toUpperCase()} FRAME`
  });
  console.log(`[Storyboard] Previewing ${frameType} frame for scene ${sceneSeq}`);
}

/**
 * Switch to the VIDEOS tab (top-level tab)
 * Task 113 Issue #6: Used when clicking VIEW ALL on video history indicator
 */
function switchToVideosTab() {
  const videosTab = document.querySelector('.prompt-tab[data-target="tab-videos"]');
  videosTab?.click();
}

/**
 * Handle input events for live editing
 */
function handleStoryboardInput(e) {
  const target = e.target;
  if (!target.dataset.field) return;

  // Mark as dirty (has unsaved changes)
  const sceneCard = target.closest('.scene-card-screenplay');
  if (sceneCard) {
    sceneCard.classList.add('has-changes');
    const saveBtn = sceneCard.querySelector('[data-action="save-scene"]');
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.classList.add('active');
    }
  }
}

/**
 * Handle change events for field updates and file uploads
 */
async function handleStoryboardChange(e) {
  const target = e.target;

  // Handle file upload inputs
  if (target.classList.contains('frame-upload-input')) {
    const file = target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const slot = target.closest('.frame-slot');
      if (slot) {
        handleLocalImageUpload(file, slot);
      }
    }
    target.value = ''; // Reset for re-upload
    return;
  }

  if (!target.dataset.field) return;

  const field = target.dataset.field;
  const value = target.value;

  // Get the active storyboard to find the idea ID
  const storyboard = getActiveStoryboard();
  const ideaId = storyboard?.ideaId || storyboard?.sourceIdea?.id;

  // Handle storyboard-level fields (style)
  if (field === 'style') {
    updateStoryboardStyle(value);
    // Save to database if this is a saved idea
    if (ideaId && isDatabaseIdea(ideaId)) {
      try {
        await saveToDatabase(ideaId);
        console.log('[Storyboard] Style saved to database');
      } catch (error) {
        console.error('[Storyboard] Failed to save style:', error);
      }
    }
    return;
  }

  // Handle scene-level fields
  const sceneSeq = parseInt(target.dataset.scene);
  if (sceneSeq) {
    updateSceneField(sceneSeq, field, value);
  }
}

// ============================================================================
// GENERATION TRIGGER
// ============================================================================

/**
 * Render loading state for generation
 * @param {number|null} sceneCount - Detected scene count (optional)
 */
function renderLoadingState(sceneCount = null) {
  const countText = sceneCount ? `${sceneCount} ` : '';
  return `
    <div class="storyboard-loading">
      <div class="loading-spinner"></div>
      <p class="loading-text">Generating ${countText}production-ready scenes...</p>
      <p class="loading-subtext">Expanding your shots for video AI</p>
    </div>
  `;
}

/**
 * Generate scenes for an idea
 */
export async function triggerSceneGeneration(idea, brief) {
  const generateBtn = document.getElementById('btn-canvas-generate-scenes')
    || document.getElementById('btn-generate-scenes');
  const container = document.getElementById('canvas-video-scenes-container')
    || document.getElementById('video-scenes-container');
  const placeholder = document.getElementById('canvas-video-scenes-placeholder')
    || document.getElementById('video-scenes-placeholder');

  // Update button state
  if (generateBtn) {
    generateBtn.classList.add('loading');
    generateBtn.disabled = true;
    generateBtn.innerHTML = '<span class="btn-icon">⏳</span><span class="btn-text">GENERATING...</span>';
  }

  // Hide placeholder, show loading
  if (placeholder) placeholder.style.display = 'none';
  if (container) {
    container.innerHTML = renderLoadingState();
  }

  try {
    await generateScenes(idea, brief);
    renderStoryboard(idea.id, idea);
  } catch (error) {
    console.error('Scene generation failed:', error);

    if (container) {
      container.innerHTML = `
        <div class="storyboard-error">
          <p class="error-title">Generation Failed</p>
          <p class="error-message">${error.message}</p>
        </div>
      `;
    }

    if (generateBtn) {
      generateBtn.classList.remove('loading');
      generateBtn.disabled = false;
      generateBtn.innerHTML = '<span class="btn-icon">⚠</span><span class="btn-text">TRY AGAIN</span>';
    }
  } finally {
    if (generateBtn) {
      generateBtn.disabled = false;
    }
  }
}

// ============================================================================
// WINDOW EXPORTS (for global access)
// ============================================================================

window.renderStoryboard = renderStoryboard;
window.triggerSceneGeneration = triggerSceneGeneration;
window.initStoryboardUI = initStoryboardUI;
window.renderPanelsView = renderPanelsView;
window.importPanelsToScenes = importPanelsToScenes;
window.renderVideosTab = renderVideosTab;
window.openPanelSelector = openPanelSelector;

// ============================================================================
// MODULE EXPORTS
// ============================================================================

export {
  renderStoryboard,
  renderPanelsView,
  renderVideosTab,
  importPanelsToScenes,
  openPanelSelector
};
